/**
 * v38.11 — Bulk Staff Import page (rewritten for the new canonical schema).
 *
 * Upload an Excel (.xlsx) or CSV master sheet to mass-provision employees with
 * generated Employee IDs (`<PREFIX>-<DEPT>-<P|C|T>-<SERIAL>`) and a temporary
 * password (Welcome@123). The login ID is the hyphenless, upper-case variant
 * (e.g. `PMSHDOCP1000`) — both forms work at /login.
 */
import { useEffect, useState } from 'react';
import { useAuth } from '../lib/auth';
import { Navigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Upload, FileSpreadsheet, Download, CheckCircle2, XCircle } from 'lucide-react';

const BulkStaffImportPage = () => {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [hospitalIdOverride, setHospitalIdOverride] = useState('');
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [errors, setErrors] = useState(null);
  const [options, setOptions] = useState(null);

  // v38.11 — Pull the live legal dept-code list so the on-page hint stays in
  // sync with what's actually in this hospital's Departments master.
  useEffect(() => {
    if (!user) return;
    const url = user.is_super_admin && hospitalIdOverride
      ? `/staff/bulk-import/options?hospital_id=${encodeURIComponent(hospitalIdOverride)}`
      : '/staff/bulk-import/options';
    api.get(url).then((res) => setOptions(res.data)).catch(() => setOptions(null));
  }, [user, hospitalIdOverride]);

  if (!user) return <Navigate to="/login" replace />;

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/staff/bulk-import/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'employee-import-template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      toast.error('Failed to download template');
    }
  };

  const handleUpload = async () => {
    if (!file) { toast.error('Please select a file first.'); return; }
    setUploading(true);
    setResult(null);
    setErrors(null);
    const fd = new FormData();
    fd.append('file', file);
    const url = user.is_super_admin && hospitalIdOverride
      ? `/staff/bulk-import?hospital_id=${encodeURIComponent(hospitalIdOverride)}`
      : '/staff/bulk-import';
    try {
      const res = await api.post(url, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(res.data);
      toast.success(`Imported ${res.data.created.length} staff. Skipped ${res.data.skipped.length}.`);
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail.errors) {
        setErrors(detail.errors);
        toast.error(detail.message || 'Validation failed');
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Import failed');
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="bulk-staff-import-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <FileSpreadsheet className="h-6 w-6 text-emerald-600" />
          Bulk Staff Upload
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Upload a master sheet (.xlsx / .csv) to mass-provision Employee IDs in the canonical format <code className="text-xs font-mono">&lt;PREFIX&gt;-&lt;DEPT&gt;-&lt;P|C|T&gt;-&lt;SERIAL&gt;</code>.
          Every imported user starts with the temporary password <code className="px-1.5 py-0.5 rounded bg-slate-100 text-emerald-700 font-mono text-xs">Welcome@123</code> and is forced to change it on first login.
        </p>
      </div>

      <Alert className="border-emerald-200 bg-emerald-50/60">
        <AlertDescription className="text-sm text-slate-700 leading-relaxed">
          <strong>Required columns:</strong> <code>first_name</code>, <code>last_name</code>, <code>email</code>, <code>department_code</code>, <code>staff_category</code>
          <br />
          <strong>Optional columns:</strong> <code>phone</code>, <code>role</code>, <code>designation</code>, <code>gender</code>, <code>dob</code>, <code>address</code>, <code>blood_group</code>
          <br />
          <strong>Staff Category</strong> must be one of: {(options?.staff_categories || ['Permanent', 'Contractual', 'Trainee']).map((c) => <code key={c} className="px-1 mx-0.5 bg-emerald-100 rounded">{c}</code>)}
          <br />
          {options?.departments?.length > 0 ? (
            <span>
              <strong>Department Codes</strong> (must match this hospital&apos;s master): {options.departments.map((d) => (
                <code key={d.code} className="px-1 mx-0.5 bg-slate-100 rounded text-slate-700" title={d.name}>{d.code}</code>
              ))}
            </span>
          ) : (
            <span className="text-rose-700"><strong>No departments configured yet.</strong> Upload your Department list under <em>Departments → Bulk Department Upload</em> first.</span>
          )}
          <br />
          <em className="text-emerald-700">Validation is atomic — if any row is invalid, NO rows are inserted.</em>
        </AlertDescription>
      </Alert>

      <Card className="p-6">
        <div className="flex flex-wrap gap-4 items-end">
          <div className="flex-1 min-w-[260px]">
            <Label htmlFor="file" className="text-sm font-medium">Master Sheet File</Label>
            <Input
              id="file"
              type="file"
              accept=".csv,.xlsx"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              className="mt-1"
              data-testid="bulk-import-file-input"
            />
            {file && <p className="text-xs text-slate-500 mt-1">{file.name} · {(file.size / 1024).toFixed(1)} KB</p>}
          </div>
          {user.is_super_admin && (
            <div className="min-w-[260px]">
              <Label htmlFor="hosp" className="text-sm font-medium">Target Hospital ID (Super-Admin only)</Label>
              <Input
                id="hosp"
                value={hospitalIdOverride}
                onChange={(e) => setHospitalIdOverride(e.target.value)}
                placeholder="UUID of target hospital"
                className="mt-1 font-mono text-xs"
              />
            </div>
          )}
          <Button onClick={downloadTemplate} variant="outline" data-testid="download-template-btn">
            <Download className="h-4 w-4 mr-2" />
            Template
          </Button>
          <Button
            onClick={handleUpload}
            disabled={!file || uploading || !options?.departments?.length}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid="bulk-import-upload-btn"
          >
            {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
            {uploading ? 'Importing…' : 'Import'}
          </Button>
        </div>
      </Card>

      {errors && (
        <Card className="p-5 border-rose-200 bg-rose-50/40" data-testid="bulk-import-errors">
          <h4 className="text-sm font-semibold text-rose-800 mb-3 flex items-center gap-2">
            <XCircle className="h-4 w-4" /> Validation failed — {errors.length} issue(s). Nothing was imported.
          </h4>
          <ul className="space-y-1 text-xs font-mono max-h-72 overflow-y-auto">
            {errors.map((e, i) => (
              <li key={i} className="text-rose-700">Row {e.row}: {e.error}</li>
            ))}
          </ul>
        </Card>
      )}

      {result && (
        <Card className="p-5 border-emerald-200 bg-emerald-50/40" data-testid="bulk-import-result">
          <h4 className="text-sm font-semibold text-emerald-800 mb-3 flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4" /> Import complete
          </h4>
          <div className="grid grid-cols-3 gap-3 text-xs text-slate-700 mb-4">
            <div><span className="text-slate-500">Rows received</span><br /><span className="font-mono text-base">{result.rows_received}</span></div>
            <div><span className="text-slate-500">Created</span><br /><span className="font-mono text-base text-emerald-700">{result.created.length}</span></div>
            <div><span className="text-slate-500">Skipped</span><br /><span className="font-mono text-base text-amber-700">{result.skipped.length}</span></div>
          </div>
          {result.created.length > 0 && (
            <details open>
              <summary className="text-xs font-medium cursor-pointer text-emerald-700 mb-2">Created Employees ({result.created.length})</summary>
              <div className="max-h-72 overflow-y-auto text-xs font-mono">
                <div className="grid grid-cols-3 gap-2 px-1 py-1 text-[10px] uppercase text-slate-400 border-b">
                  <span>Email</span><span>Employee ID (display)</span><span>Login ID</span>
                </div>
                {result.created.map((c, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 border-b border-emerald-100 py-1">
                    <span className="text-slate-600 truncate">{c.email}</span>
                    <span className="text-emerald-700">{c.employee_id}</span>
                    <span className="text-emerald-700 font-semibold">{c.login_id}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
          {result.skipped.length > 0 && (
            <details className="mt-3">
              <summary className="text-xs font-medium cursor-pointer text-amber-700 mb-2">Skipped Rows ({result.skipped.length})</summary>
              <div className="max-h-72 overflow-y-auto text-xs font-mono">
                {result.skipped.map((s, i) => (
                  <div key={i} className="flex justify-between border-b border-amber-100 py-1">
                    <span className="text-slate-600">Row {s.row}: {s.email}</span>
                    <span className="text-amber-700">{s.reason}</span>
                  </div>
                ))}
              </div>
            </details>
          )}
        </Card>
      )}
    </div>
  );
};

export default BulkStaffImportPage;
